import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import ErrorList from 'Components/UI/ErrorList';
import MetaField from './MetaControlField';
import useMetaLayoutContext, {
  MetaLayoutContextProvider,
} from './MetaLayoutContext';

export const RemainingFieldList = ({ excludedFields, ...props }) => {
  const { remainingFields, resource } = useMetaLayoutContext();
  const names = [...remainingFields].filter(n => !excludedFields.includes(n));

  return names.map(f => (
    <div key={`${resource.id()}-form-${f}`} {...props}>
      <MetaField resource={resource} attr={f} notInLayout />
    </div>
  ));
};

const styles = (theme) => ({
  root: {
    width: '100%',
  },
  errorList: {
    margin: props => (props?.errors?.length !== 0 ? theme.spacing(2, 0) : 0),
  },
  errorListHidden: {
    display: 'none',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flex: '1',
  },
});

function MetaLayout({
  resource,
  layout,
  classes,
  onChange,
  errors,
  metaOverrideSet,
  LayoutProps,
  onErrorLinkClick,
  ...rest
}) {
  const LayoutComponent = layout;
  const {
    excludedFromRemainingFields = [],
    hideErrorList,
    onlyUnsourcedErrors,
    showRemainingFieldList,
    applyMetaOverrideSet,
    errorListClassName,
    onlyRenderLayout,
    noExtraMarkup,
    ErrorListProps,
    ...layoutRest
  } = LayoutProps || {};

  let errs = errors;
  if (onlyUnsourcedErrors) {
    errs = errors.filter(el => !el.source);
  }

  return (
    <MetaLayoutContextProvider
      resource={resource}
      onChange={onChange}
      errors={errors}
      unsourcedErrors={errs}
      metaOverrideSet={metaOverrideSet}
      applyMetaOverrideSet={applyMetaOverrideSet}
      {...rest}
    >
      {onlyRenderLayout || noExtraMarkup ? (
        <LayoutComponent {...layoutRest} />
      ) : (
        <div className={classes.root}>
          <LayoutComponent {...layoutRest} />
          {showRemainingFieldList && (
            <RemainingFieldList
              className={classes.row}
              excludedFields={excludedFromRemainingFields}
            />
          )}
        </div>
      )}
      {noExtraMarkup ? null : <ErrorList
        className={classNames(classes.errorList, {
          [classes.errorListHidden]: hideErrorList,
        }, errorListClassName)}
        errors={errs}
        hideErrorTitle
        onErrorLinkClick={onErrorLinkClick}
        {...ErrorListProps}
      />}
    </MetaLayoutContextProvider>
  );
}

MetaLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
  layout: PropTypes.any.isRequired,
  LayoutProps: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.array,
  metaOverrideSet: PropTypes.object,
  showRemainingFieldList: PropTypes.bool,
  errorListClassName: PropTypes.string,
  onErrorLinkClick: PropTypes.func,
};

MetaLayout.defaultProps = {
  errors: [],
  metaOverrideSet: null,
  LayoutProps: {
    excludedFromRemainingFields: [],
    hideErrorList: false,
    onlyRenderLayout: false,
  },
  showRemainingFieldList: true,
  errorListClassName: undefined,
  onErrorLinkClick: null,
};

export default withStyles(styles, { name: 'MetaLayout' })(MetaLayout);
